import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { adminCompaniesService } from '../../companies/services/admin-companies.service';
import { AdminCompany } from '../../companies/types';
import { COLORS } from '../../../../core/constants/colors';

export const AdminEnrichmentSelectorPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const data = await adminCompaniesService.getAllCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCompany = (companyId: number) => {
    navigate(`/admin/enrichment/${companyId}`);
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = ['#713FFF', '#FF5E2E', '#0E81FF', '#8CE58C'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  return (
    <AdminLayout pageTitle="Company Enrichment" pageDescription="Select a company to run company enrichment">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-6xl mx-auto px-8 py-8">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              />
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: '#9CA3AF' }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>

          {/* Companies List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div
                  className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
                  style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                />
                <p style={{ color: '#6B7280' }}>Loading companies...</p>
              </div>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                style={{ color: '#9CA3AF' }}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-lg font-medium mb-2" style={{ color: '#1F2937' }}>
                {searchTerm ? 'No companies found' : 'No companies available'}
              </p>
              <p className="text-sm" style={{ color: '#6B7280' }}>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Companies will appear here once they are created'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelectCompany(company.id)}
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all text-left border hover:border-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                      style={{ backgroundColor: getInitialColor(company.name) }}
                    >
                      {getCompanyInitial(company.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base mb-1 truncate" style={{ color: '#1F2937' }}>
                        {company.name}
                      </h3>
                      {company.industry && (
                        <p className="text-sm mb-2 truncate" style={{ color: '#6B7280' }}>
                          {company.industry}
                        </p>
                      )}
                      {company.location && (
                        <p className="text-xs truncate" style={{ color: '#9CA3AF' }}>
                          📍 {company.location}
                        </p>
                      )}
                    </div>
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: '#9CA3AF' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
